import { useParams, Link } from "react-router-dom";
import { useEffect } from "react";
import { useSEO } from "@/hooks/useSEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { comics, categories } from "@/mocks/comics";

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 text-center">
            <h1 className="text-2xl font-bold text-foreground-950">漫画未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return <ComicDetailContent comic={comic} />;
}

function ComicDetailContent({ comic }: { comic: typeof comics[0] }) {
  const categoryObj = categories.find((c) => c.slug === comic.category);
  const categoryName = categoryObj?.name || comic.category;
  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 4);
  const chapterNumbers = Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => comic.chapters - i);

  useSEO({
    title: `${comic.title} - 免费在线韩国漫画 | 韩漫屋`,
    description: `${comic.title}，作者${comic.author}，${comic.description.slice(0, 120)}... 免费在线韩国漫画阅读，尽在韩漫屋。`,
    keywords: `${comic.title},${comic.author},韩国漫画,韩漫,免费在线漫画,${categoryName}`,
    canonicalUrl: `https://hanmanwu.com/comic/${comic.id}`,
    ogTitle: `${comic.title} - 免费在线韩国漫画 | 韩漫屋`,
    ogDescription: comic.description.slice(0, 150),
    ogUrl: `https://hanmanwu.com/comic/${comic.id}`,
    ogType: "article",
    ogImage: comic.coverImage,
    twitterCard: "summary_large_image",
    schemaObjects: [{
      "@context": "https://schema.org",
      "@type": "ComicStory",
      name: comic.title,
      author: { "@type": "Person", name: comic.author },
      description: comic.description,
      image: comic.coverImage,
      url: `https://hanmanwu.com/comic/${comic.id}`,
      genre: categoryName,
      datePublished: "2024-01-01",
      publisher: {
        "@type": "Organization",
        name: "韩漫屋",
      },
    }],
  });

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: categoryName, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <article className="py-8 md:py-12">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12">
              <div className="flex-shrink-0 w-full md:w-72">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩国漫画封面`}
                    title={`${comic.title} - 免费在线韩国漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-600 mt-2">
                  作者：<strong>{comic.author}</strong>
                </p>

                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                    <i className="ri-star-fill w-3 h-3 flex items-center justify-center"></i>
                    {comic.rating} 分
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    comic.status === "连载中"
                      ? "bg-primary-100 text-primary-700"
                      : "bg-foreground-200 text-foreground-700"
                  }`}>
                    {comic.status}
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                    {comic.chapters} 话
                  </span>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-background-200/70 text-foreground-600">
                    {(comic.views / 10000).toFixed(0)} 万人气
                  </span>
                </div>

                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded text-xs bg-background-100 text-foreground-700 border border-background-200/70 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6">
                  <h2 className="text-base font-semibold text-foreground-950 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
                </div>

                <div className="flex items-center gap-3 mt-6">
                  <button className="inline-flex items-center px-6 py-3 rounded-md text-sm font-semibold whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer">
                    <i className="ri-play-circle-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    开始阅读
                  </button>
                  <button className="inline-flex items-center px-6 py-3 rounded-md text-sm font-semibold whitespace-nowrap bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer border border-background-200/70">
                    <i className="ri-bookmark-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                    收藏
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-12">
              <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                章节列表
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {chapterNumbers.map((ch) => (
                  <a
                    key={ch}
                    href="#"
                    className="flex items-center justify-between p-3 rounded-md bg-background-100 border border-background-200/70 hover:border-primary-300 hover:bg-background-200/70 transition-all cursor-pointer"
                    onClick={(e) => e.preventDefault()}
                  >
                    <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">第 {ch} 话</span>
                    <i className="ri-arrow-right-s-line text-foreground-400 w-4 h-4 flex items-center justify-center"></i>
                  </a>
                ))}
                {comic.chapters > 20 && (
                  <div className="flex items-center justify-center p-3 rounded-md bg-background-100 border border-dashed border-background-300 text-sm text-foreground-500">
                    更多章节...
                  </div>
                )}
              </div>
            </div>

            {relatedComics.length > 0 && (
              <div className="mt-14">
                <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  猜你喜欢
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                  {relatedComics.map((c) => (
                    <Link
                      key={c.id}
                      to={`/comic/${c.id}`}
                      className="group block cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                        <img
                          src={c.coverImage}
                          alt={c.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {c.title}
                      </h3>
                      <p className="text-xs text-foreground-600 mt-1">{c.author}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}